import { v4 as uuidv4 } from 'uuid';
import { fetchWithSchema } from './api';
import { getAppNamespace, getAppDisplayName } from './appIdentity';
import { getSchemaScopedStorageKey } from './schemaStorage';

// Detect referral from URL immediately when module loads (before React mounts)
// This ensures referral is available when registerSession is called
if (typeof window !== 'undefined') {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get('ref');
    if (refParam) {
      const normalized = refParam.trim().toLowerCase();
      const validSources = ['facebook', 'instagram', 'google', 'linkedin', 'tiktok', 'youtube', 'twitter', 'x'];
      const finalSource = normalized === 'twitter' ? 'x' : normalized;
      
      if (validSources.includes(finalSource)) {
        const referralKey = getSchemaScopedStorageKey('pending_referral');
        sessionStorage.setItem(referralKey, JSON.stringify({
          source: finalSource,
          detectedAt: new Date().toISOString()
        }));
        console.log('🔗 [SessionManager Module] Detected referral from URL on module load:', finalSource);
      }
    }
  } catch (error) {
    // Silently fail - referral detection is not critical
  }
}

export interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
  timestamp: string;
  version: string;
  sessionId?: string; // Pentru utilizatori anonimi
}

interface SessionData {
  sessionId: string;
  createdAt: string;
  lastActivity: string;
  consent?: CookieConsent;
  referralSource?: string | null;
}

class SessionManager {
  private static instance: SessionManager;
  private namespace = getAppNamespace();
  private displayName = getAppDisplayName();

  private get sessionKey() {
    const baseKey = `${this.namespace}_session`;
    return getSchemaScopedStorageKey(baseKey);
  }

  private get sessionRegisteredKey() {
    const baseKey = `${this.namespace}_session_registered`;
    return getSchemaScopedStorageKey(baseKey);
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
      // Detect referral from URL immediately when SessionManager is initialized
      SessionManager.instance.detectAndStoreReferralFromURL();
    }
    return SessionManager.instance;
  }

  setNamespace(appName?: string | null) {
    const nextNamespace = getAppNamespace(appName);
    const nextDisplay = getAppDisplayName(appName);
    this.namespace = nextNamespace;
    this.displayName = nextDisplay;
  }

  // Generează sau preia Session ID (fără a-l sincroniza automat cu serverul)
  getSessionId(): string {
    const localSessionId = this.getStoredSessionId();
    if (localSessionId) {
      return localSessionId;
    }

    const newSessionId = uuidv4();
    this.persistSessionId(newSessionId);
    return newSessionId;
  }

  // Detect and store referral from URL synchronously (called before session registration)
  private detectAndStoreReferralFromURL(): void {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const refParam = urlParams.get('ref');
      if (refParam) {
        // Normalize and validate
        const normalized = refParam.trim().toLowerCase();
        const validSources = ['facebook', 'instagram', 'google', 'linkedin', 'tiktok', 'youtube', 'twitter', 'x'];
        const finalSource = normalized === 'twitter' ? 'x' : normalized;
        
        if (validSources.includes(finalSource)) {
          const referralKey = getSchemaScopedStorageKey('pending_referral');
          sessionStorage.setItem(referralKey, JSON.stringify({
            source: finalSource,
            detectedAt: new Date().toISOString()
          }));
          console.log('🔗 [SessionManager] Detected referral from URL synchronously:', finalSource);
        }
      }
    } catch (error) {
      console.warn('Failed to detect referral from URL:', error);
    }
  }

  private getStoredSessionId(): string | null {
    try {
      const sessionData = localStorage.getItem(this.sessionKey);
      if (sessionData) {
        const parsed: SessionData = JSON.parse(sessionData);
        return parsed.sessionId;
      }
    } catch (error) {
      console.warn('Failed to parse session data from localStorage:', error);
    }
    return null;
  }

  private persistSessionId(sessionId: string): void {
    // Salvează în localStorage
    const sessionData: SessionData = {
      sessionId,
      createdAt: new Date().toISOString(),
      lastActivity: new Date().toISOString()
    };

    localStorage.setItem(this.sessionKey, JSON.stringify(sessionData));
  }

  private async registerSessionWithServer(sessionId: string, referralSource?: string | null): Promise<void> {
    try {
      const alreadyRegistered = localStorage.getItem(this.sessionRegisteredKey) === 'true';
      
      // If no referral source provided, try to get it from sessionStorage
      let finalReferralSource = referralSource;
      if (!finalReferralSource) {
        try {
          const referralKey = getSchemaScopedStorageKey('pending_referral');
          const pendingReferral = sessionStorage.getItem(referralKey);
          if (pendingReferral) {
            const parsed = JSON.parse(pendingReferral);
            if (parsed.source) {
              finalReferralSource = parsed.source;
              console.log('🔗 [SessionManager] Found pending referral in sessionStorage:', finalReferralSource);
            }
          }
        } catch (error) {
          console.warn('Failed to parse pending referral from sessionStorage:', error);
        }
      }
      
      const requestBody: any = {
        sessionId,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        appName: this.displayName
      };

      // Include referralSource if provided or found in sessionStorage
      if (finalReferralSource) {
        requestBody.referralSource = finalReferralSource;
        console.log('🔗 [SessionManager] Registering session with referral:', finalReferralSource);
      }

      // If already registered but we have a referral source, we still want to update
      // The backend upsert will handle updating the referral_source
      if (alreadyRegistered && !finalReferralSource) {
        console.log('🔗 [SessionManager] Session already registered, skipping');
        return;
      }

      const response = await fetchWithSchema('/api/session/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!alreadyRegistered) {
        localStorage.setItem(this.sessionRegisteredKey, 'true');
      }
      console.log('🔗 [SessionManager] Session registered successfully');
    } catch (error) {
      console.warn('Failed to register session with server:', error);
    }
  }

  // Actualizează activitatea sesiunii
  updateActivity(): void {
    const sessionId = this.getSessionId();
    if (sessionId) {
      const sessionData = this.getLocalSessionData();
      if (sessionData) {
        sessionData.lastActivity = new Date().toISOString();
        localStorage.setItem(this.sessionKey, JSON.stringify(sessionData));
      }
    }
  }

  // Salvează consimțământul pentru sesiune
  async saveConsentForSession(consent: CookieConsent, referralSource?: string | null): Promise<void> {
    const sessionId = this.getSessionId();
    this.storeConsentLocally(sessionId, consent);
    await this.registerSessionWithServer(sessionId, referralSource);
    await this.sendConsentToServer(sessionId, consent, referralSource);
  }

  private storeConsentLocally(sessionId: string, consent: CookieConsent) {
    const sessionData = this.getLocalSessionData() || {
      sessionId,
      createdAt: new Date().toISOString(),
      lastActivity: new Date().toISOString()
    };

    sessionData.consent = consent;
    sessionData.lastActivity = new Date().toISOString();

    localStorage.setItem(this.sessionKey, JSON.stringify(sessionData));
  }

  private async sendConsentToServer(sessionId: string, consent: CookieConsent, referralSource?: string | null): Promise<void> {
    try {
      const requestBody: any = {
        sessionId,
        consent,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        appName: this.displayName
      };

      // Include referralSource if provided
      if (referralSource) {
        requestBody.referralSource = referralSource;
      }

      const response = await fetchWithSchema('/api/session/consent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.warn('Failed to save consent to server:', error);
      // Nu aruncăm eroarea - funcționăm offline cu localStorage
    }
  }

  // Preia consimțământul pentru sesiune
  async getConsentForSession(): Promise<CookieConsent | null> {
    const sessionId = this.getStoredSessionId();
    if (!sessionId) {
      return null;
    }

    // Verifică mai întâi localStorage
    const sessionData = this.getLocalSessionData();
    if (sessionData?.consent) {
      return sessionData.consent;
    }

    // Încearcă să preia de pe server
    try {
      const response = await fetchWithSchema(`/api/session/consent/${sessionId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.consent) {
          this.storeConsentLocally(sessionId, data.consent);
          return data.consent;
        }
      }
    } catch (error) {
      console.warn('Failed to fetch consent from server:', error);
    }

    return null;
  }

  private getLocalSessionData(): SessionData | null {
    try {
      const sessionData = localStorage.getItem(this.sessionKey);
      return sessionData ? JSON.parse(sessionData) : null;
    } catch {
      return null;
    }
  }

  // Șterge sesiunea (pentru reset consimțământ)
  async clearSession(): Promise<void> {
    const sessionId = this.getStoredSessionId();
    localStorage.removeItem(this.sessionKey);
    localStorage.removeItem(this.sessionRegisteredKey);

    if (!sessionId) {
      return;
    }

    await this.sendClearSessionToServer(sessionId);
  }

  private async sendClearSessionToServer(sessionId: string): Promise<void> {
    try {
      await fetchWithSchema('/api/session/clear', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId, appName: this.displayName })
      });
    } catch (error) {
      console.warn('Failed to clear session on server:', error);
    }
  }

  // Verifică dacă sesiunea este validă (nu a expirat)
  isSessionValid(): boolean {
    const sessionData = this.getLocalSessionData();
    if (!sessionData) return false;

    const lastActivity = new Date(sessionData.lastActivity);
    const now = new Date();
    const diffInDays = (now.getTime() - lastActivity.getTime()) / (1000 * 60 * 60 * 24);

    // Sesiunea expiră după 30 de zile de inactivitate
    return diffInDays < 30;
  }
}

export const sessionManager = SessionManager.getInstance();
